import React, { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import { Image, Button } from "react-bootstrap";
import { ALLOWED_IMAGE_TYPES, CONSTANTS } from "../../utils/Constant/Constant";
import CompressImage from "./Compressimage";

// 1MB
const MAX_FILE_SIZE = 1024 * 1024; 

const readOrCompressFile = async (file, maxSizeBytes) => {
  if (file.size > maxSizeBytes) {
    console.log(
      `[TemplateSessionCard] File size ${file.size} exceeds ${maxSizeBytes}. Compressing...`,
    );
    const compressed = await CompressImage(file, maxSizeBytes);
    console.log("[TemplateSessionCard] Compression done:", compressed);
    return {
      base64Image: compressed.base64,
      fileType: compressed.fileType,
      mediaType: compressed.mediaType,
      fileName: file.name.replace(/\.[^/.]+$/, ".jpg"),
    };
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      let base64Image = reader.result;
      if (base64Image && base64Image.startsWith("data:")) {
        base64Image = base64Image.split(",")[1];
      }
      resolve({
        base64Image,
        fileType: file.type,
        mediaType: file.type.split("/")[0],
        fileName: file.name,
      });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const TemplateSessionCard = ({
  session,
  index,
  activeSessionId,
  placeholderFrontImage,
  handleSessionClick,
  handleImageRemoveSessionTemplate,
  handleImageChangeSessionTemplate,
  handleFileInputReset,
  handleRemoveSession,
  setSessions,
  moveSession,
  setToastConfig,
}) => {
  const ref = useRef(null);

  const [{ isDragging }, drag] = useDrag({
    type: "SESSION",
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: "SESSION",
    hover: (item, monitor) => {
      if (!ref.current) return;
      
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) return;

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      moveSession(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  drag(drop(ref));

  // Validates type, then compresses if needed
  const handleImageChangeWithValidation = async (e, id) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Block GIFs and non-static image types
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      e.target.value = "";
      setToastConfig({
        type: CONSTANTS.TOAST_ERROR,
        heading: "Invalid File Type",
        message:
          "Only static images are allowed (JPEG, PNG, WEBP). GIFs and animations are not supported.",
      });
      return;
    }

    try {
      const { base64Image, fileType, mediaType, fileName } =
        await readOrCompressFile(file, MAX_FILE_SIZE);

      // Apply directly to sessions state — no need to call parent handler
      setSessions((prevSessions) =>
        prevSessions.map((session) =>
          session.id === id
            ? {
                ...session,
                sessionPhotoFileName: fileName,
                sessionPhotoMediaType: mediaType,
                sessionPhotoFileType: fileType,
                sessionPhotoBase64: base64Image,
                sessionPhotoSourceType: "upload",
              }
            : session,
        ),
      );
    } catch (err) {
      console.error("[TemplateSessionCard] Image processing failed:", err);
      e.target.value = "";
      setToastConfig({
        type: CONSTANTS.TOAST_ERROR,
        heading: "Upload Failed",
        message: "Failed to process image. Please try another file.",
      });
    }
  };

  return (
    <li
      ref={ref}
      key={session.id}
      className={`session-item ${
        activeSessionId === session.id ? "active" : ""
      }`}
      style={{ opacity: isDragging ? 0.5 : 1, cursor: "grab" }}
      onClick={() => handleSessionClick(session.id)}
    >
      <div>
        <div className="mx-3 mt-1 image-container-sessions position-relative profile-image-container">
          {session?.sessionPhotoBase64 && session.sessionPhotoBase64.length > 0  ? (
            <Image
              src={`data:${session?.sessionPhotoFileType};base64,${session?.sessionPhotoBase64}`}
              alt="Session Image"
              className="w-100 h-100 rounded-3"
            />
          ) : (
            <Image
              src={placeholderFrontImage}
              alt="Default Session"
              className="w-100 h-100 rounded-3 p-1"
            />
          )}

          {session?.sessionPhotoBase64 && session.sessionPhotoBase64.length > 0 ? (
            <Button
              variant="link"
              className="position-absolute m-2 p-0 icon-position-session"
              onClick={(e) => {
                e.stopPropagation();
                handleImageRemoveSessionTemplate(session.id);
              }}
            >
              <i
                className="bi bi-x-circle-fill"
                style={{ fontSize: "30px", color: "red" }}
              ></i>
            </Button>
          ) : (
            <Button
              variant="link"
              className="position-absolute m-2 p-0 icon-position-session"
              onClick={(e) => {
                e.stopPropagation();
                document
                  .getElementById(`workoutTemplateSession-${session.id}`)
                  .click();
              }}
            >
              <i
                className="bi bi-plus-circle-fill"
                style={{ fontSize: "30px", color: "green" }}
              ></i>
            </Button>
          )}

          {/* accept only static image types — no gif */}
          <input
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            id={`workoutTemplateSession-${session.id}`}
            className="mt-2"
            style={{ display: "none" }}
            onChange={(e) => handleImageChangeWithValidation(e, session.id)}
            onClick={() =>
              handleFileInputReset(`workoutTemplateSession-${session.id}`)
            }
          />
        </div>
      </div>
      <input
        type="text"
        value={session.name}
      onChange={(e) => {
      setSessions((prevSessions) =>
        prevSessions.map((s, idx) => {
          return s.id === session.id
            ? { 
                ...s, 
                name: e.target.value,
                // Preserve existing indexOrder, don't recalculate
                indexOrder: s.indexOrder !== undefined && s.indexOrder !== null ? s.indexOrder : idx
              }
            : s; // Don't modify other sessions at all
        })
      );
    }}
        className="session-name-input"
      />

      <span
        className="remove-session"
        onClick={(e) => {
          e.stopPropagation();
          handleRemoveSession(session.id);
        }}
      >
        X
      </span>
    </li>
  );
};

export default TemplateSessionCard;